import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ProgramFixedContentRow, ProgramFixedContentRowUpsert } from '../types/domain';
import {
  deleteProgramFixedContentRowsByIds,
  saveProgramFixedContentRowsDiff,
  upsertProgramFixedContentRows,
} from '../services/programFixedContentService';
import { invalidateProgramFixedRowsQuery, setProgramFixedRowsQueryData } from '../query/cacheUpdates';
import { schoolKeys } from '../query/keys';
import { useProgramFixedRowsQuery } from '../query/hooks/useProgramFixedRowsQuery';

interface UseProgramFixedContentRowsParams {
  workspaceId: string;
  academicYear: number;
  termNumber: 1 | 2 | null;
  gradeId: string;
  /** 외부에서 저장 완료 등으로 목록만 다시 불러올 때 증가 */
  refreshNonce?: number;
  isActive?: boolean;
}

export interface ProgramFixedContentRowsState {
  loading: boolean;
  error: string | null;
  rows: ProgramFixedContentRow[];
  upsertRows: (rows: ProgramFixedContentRowUpsert[]) => Promise<void>;
  /** 표 전체 스냅샷을 저장본으로: 현재 목록과의 차이(변경·추가·삭제)만 반영하고 목록을 결과로 교체 */
  saveSnapshotRows: (snapshot: ProgramFixedContentRowUpsert[]) => Promise<void>;
  deleteRowsByIds: (ids: string[]) => Promise<void>;
}

export function useProgramFixedContentRows(params: UseProgramFixedContentRowsParams): ProgramFixedContentRowsState {
  const { workspaceId, academicYear, termNumber, gradeId, refreshNonce = 0, isActive = true } = params;
  const queryClient = useQueryClient();
  const lastRefreshNonceRef = React.useRef(refreshNonce);
  const enabled = isActive && !!workspaceId && !!gradeId && !!termNumber;
  const tn = termNumber as 1 | 2;

  const query = useProgramFixedRowsQuery({
    workspaceId,
    academicYear,
    termNumber: tn,
    gradeId,
    enabled,
  });

  React.useEffect(() => {
    if (!enabled || refreshNonce <= lastRefreshNonceRef.current) return;
    lastRefreshNonceRef.current = refreshNonce;
    invalidateProgramFixedRowsQuery(workspaceId, academicYear, tn, gradeId);
  }, [academicYear, enabled, gradeId, refreshNonce, tn, workspaceId]);

  const upsertRows = React.useCallback(
    async (upserts: ProgramFixedContentRowUpsert[]) => {
      if (!upserts.length || !termNumber) return;
      try {
        const saved = await upsertProgramFixedContentRows(upserts);
        const prev = query.data ?? [];
        const next = [...prev];
        saved.forEach((row) => {
          const idx = next.findIndex((v) => v.id === row.id);
          if (idx >= 0) next[idx] = row;
          else next.push(row);
        });
        const sorted = next.sort(
          (a, b) => (a.order_index - b.order_index) || (a.created_at ?? '').localeCompare(b.created_at ?? '')
        );
        setProgramFixedRowsQueryData(workspaceId, academicYear, tn, gradeId, sorted);
        queryClient.setQueryData(schoolKeys.programFixedRows(workspaceId, academicYear, tn, gradeId), sorted);
      } catch (e) {
        throw e instanceof Error ? e : new Error(String(e));
      }
    },
    [academicYear, gradeId, query.data, queryClient, termNumber, tn, workspaceId]
  );

  const saveSnapshotRows = React.useCallback(
    async (snapshot: ProgramFixedContentRowUpsert[]) => {
      if (!termNumber) return;
      const next = await saveProgramFixedContentRowsDiff({
        workspaceId,
        academicYear,
        termNumber: tn,
        gradeId,
        baselineRows: query.data ?? [],
        snapshotRows: snapshot,
      });
      queryClient.setQueryData(schoolKeys.programFixedRows(workspaceId, academicYear, tn, gradeId), next);
    },
    [academicYear, gradeId, query.data, queryClient, termNumber, tn, workspaceId]
  );

  const deleteRowsByIds = React.useCallback(
    async (ids: string[]) => {
      const targetIds = ids.map((v) => v.trim()).filter(Boolean);
      if (!targetIds.length || !termNumber) return;
      try {
        await deleteProgramFixedContentRowsByIds(targetIds);
        const next = (query.data ?? []).filter((row: ProgramFixedContentRow) => !targetIds.includes(row.id));
        setProgramFixedRowsQueryData(workspaceId, academicYear, tn, gradeId, next);
        queryClient.setQueryData(schoolKeys.programFixedRows(workspaceId, academicYear, tn, gradeId), next);
      } catch (e) {
        throw e instanceof Error ? e : new Error(String(e));
      }
    },
    [academicYear, gradeId, query.data, queryClient, termNumber, tn, workspaceId]
  );

  return {
    loading: enabled ? query.isLoading : false,
    error: query.error instanceof Error ? query.error.message : query.error ? String(query.error) : null,
    rows: query.data ?? [],
    upsertRows,
    saveSnapshotRows,
    deleteRowsByIds,
  };
}
